import { Patch } from "rfc6902";
import IErrorInfo from "../models/account/IErrorInfo";
import ICreateUpdateParcel from "../models/parcel/ICreateUpdateParcel";
import { IAuctionParcels } from "../models/parcel/IParcel";
import {
    IParcelBidDict,
    IParcelBidRequest,
    ISubmitParcelBidApiResponse
} from "../models/parcel/IParcelBid";
import { IParcelBidLog } from "../models/parcel/IParcelBidLog";
import {
    ISortParcel,
    IUpdateParcelOrderApiResponse
} from "../models/parcel/ISortParcel";
import HttpProvider from "../providers/HttpProvider";
import { AppInsightsLogger } from "../components/shared/logging/AppInsightsLogger";

export default class ParcelApi {
    public static async GetParcelBidLogs(
        parcelId: string
    ): Promise<IParcelBidLog[]> {
        const uriStr = `${this.apiUrl}/get-parcel-bid-logs`;
        const queryParams = { parcelId };

        const response = await HttpProvider.GetRequest(uriStr, queryParams);
        if (!response.ok) {
            return [];
        }

        return await response.json();
    }

    public static async GetParcels(
        url: string
    ): Promise<IAuctionParcels | null> {
        const uriStr = `${this.apiUrl}/get-parcels`;
        const queryParams = { url };

        const response = await HttpProvider.GetRequest(uriStr, queryParams);
        if (!response.ok) {
            return null;
        }

        return await response.json();
    }

    public static async GetParcelBids(
        auctionId: string
    ): Promise<IParcelBidDict> {
        const uriStr = `${this.apiUrl}/get-parcel-bids`;
        const queryParams = { auctionId };

        const response = await HttpProvider.GetRequest(uriStr, queryParams);
        if (!response.ok) {
            return {};
        }

        return await response.json();
    }

    public static async CreateParcel(
        newParcel: ICreateUpdateParcel
    ): Promise<IErrorInfo[]> {
        const uriStr = `${this.apiUrl}/create`;

        const response = await HttpProvider.PostRequest(uriStr, newParcel);
        if (!response.ok) {
            return (await response.json()) as IErrorInfo[];
        }

        return [];
    }

    public static async UpdateParcel(
        id: string,
        parcelPatch: Patch,
        bidders: string[]
    ): Promise<IErrorInfo[]> {
        const uriStr = `${this.apiUrl}/update/${id}`;

        const response = await HttpProvider.PatchRequest(uriStr, {
            parcelPatch,
            bidders
        });
        if (!response.ok) {
            return (await response.json()) as IErrorInfo[];
        }

        return [];
    }

    public static async ArchiveParcel(
        id: string,
        isArchiving: boolean
    ): Promise<IErrorInfo[]> {
        const uriStr = `${this.apiUrl}/archive/${id}?isArchiving=${isArchiving}`;

        const response = await HttpProvider.PatchRequest(uriStr, {});
        if (!response.ok) {
            return (await response.json()) as IErrorInfo[];
        }

        return [];
    }

    public static async DeleteParcel(id: string): Promise<IErrorInfo[]> {
        const uriStr = `${this.apiUrl}/delete/${id}`;

        const response = await HttpProvider.DeleteRequest(uriStr);
        if (!response.ok) {
            return (await response.json()) as IErrorInfo[];
        }

        return [];
    }

    public static async UpdateParcelOrder(
        sortParcel: ISortParcel[]
    ): Promise<IUpdateParcelOrderApiResponse> {
        const uriStr = `${this.apiUrl}/update-parcel-order`;

        const response = await HttpProvider.PostRequest(uriStr, sortParcel);
        const result = {} as IUpdateParcelOrderApiResponse;

        if (response.ok) {
            result.parcels = await response.json();
        } else {
            result.errors = await response.json();
        }

        return result;
    }

    public static async SubmitParcelBid(
        request: IParcelBidRequest
    ): Promise<ISubmitParcelBidApiResponse> {
        const uriStr = `${this.apiUrl}/submit-bid`;

        const response = await HttpProvider.PostRequest(uriStr, request);
        const result = {} as ISubmitParcelBidApiResponse;

        try {
            // Adding some bespoke error handling here, because we saw an unexplained JSON parse error during load testing. --Chris Nissen, 2019-10-22
            if (response.ok) {
                result.bidResponse = await response.json();
            } else {
                result.errors = await response.json();
            }
        } catch (e) {
            AppInsightsLogger.trackException({
                exception: new Error(e as string)
            });
            return {
                bidResponse: null,
                errors: [
                    {
                        code: "General_InternalServerError",
                        description: "An internal server error has occured."
                    }
                ]
            };
        }

        return result;
    }

    public static async GetBidStatus(
        bidId: string
    ): Promise<ISubmitParcelBidApiResponse> {
        const uriStr = `${this.apiUrl}/get-bid-status`;

        const response = await HttpProvider.GetRequest(uriStr, { bidId });
        const result = {} as ISubmitParcelBidApiResponse;

        if (response.ok) {
            result.bidResponse = await response.json();
        } else {
            result.errors = await response.json();
        }

        return result;
    }

    public static async SyncSalesSite(id: string): Promise<IErrorInfo[]> {
        const uriStr = `${this.apiUrl}/sync-sales-site/${id}`;
        const response = await HttpProvider.PostRequest(uriStr);
        if (!response.ok) {
            return [
                {
                    description:
                        "Unable to sync the sales site, please try again later.",
                    code: ""
                }
            ];
        }

        return [];
    }

    private static readonly apiUrl = "/api/parcel";
}
